import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://paulalopes.legal';

const legalServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'LegalService',
  name: 'Legal Findings and Studies, Lda.',
  alternateName: 'Paula Lopes – Advogada',
  description:
    'Professional legal services in Angola including corporate law, contract management, and legal consultation. Serving businesses and individuals with 10+ years of expertise.',
  url: SITE_URL,
  telephone: '+(244) 923 502 981',
  email: 'paula.lopes@lf.co.ao',
  founder: {
    '@type': 'Person',
    name: 'Paula Lopes',
    jobTitle: 'Advogada',
    url: SITE_URL,
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Luanda',
    addressCountry: 'AO',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -8.8368,
    longitude: 13.2343,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '10:00',
      closes: '14:00',
    },
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'Country',
    name: 'Angola',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Legal Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Legal Consultation',
          description: 'Expert legal advice for businesses and individuals.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Corporate Law',
          description: 'Comprehensive corporate legal services and compliance.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Contract Law',
          description: 'Contract drafting, review, and management services.',
        },
      },
    ],
  },
  sameAs: [],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Paula Lopes – Advogada Angola | Legal Findings and Studies, Lda.',
  description:
    'Expert legal services in Angola: corporate law, contract management, and legal consultation. Professional advogada with 10+ years of experience.',
  url: SITE_URL,
  inLanguage: 'en',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Legal Findings and Studies, Lda.',
    url: SITE_URL,
  },
  about: {
    '@type': 'LegalService',
    name: 'Legal Findings and Studies, Lda.',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What legal services does Paula Lopes offer in Angola?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Paula Lopes and Legal Findings and Studies, Lda. offer a full range of legal services in Angola including corporate law, contract drafting and management, legal consultation, and advisory services for businesses and individuals.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I schedule a legal consultation with Paula Lopes?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can schedule a consultation by contacting us via phone at +(244) 923 502 981 or by email at paula.lopes@lf.co.ao. You can also use the contact form on our website.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is Legal Findings and Studies, Lda. located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Legal Findings and Studies, Lda. is based in Luanda, Angola, providing professional legal services across the country.',
      },
    },
  ],
};

function injectSchema(id: string, schema: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
}

export default function SeoJsonLd() {
  useEffect(() => {
    injectSchema('schema-legal-service', legalServiceSchema);
    injectSchema('schema-web-page', webPageSchema);
    injectSchema('schema-faq', faqSchema);

    return () => {
      ['schema-legal-service', 'schema-web-page', 'schema-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
}
